import { Injectable, computed, effect, inject, signal } from '@angular/core';

import {
    ColorMode, ColorModePreference, colorModePreference, DEFAULT_THEME, MODE_ATTRIBUTES, normalizeTheme,
    remappableColorTokens, SEMANTIC_TOKENS, SemanticTokenName, Theme, ThemeSettings,
} from './theme-token-catalog';
import { ThemeApiService } from './theme-api.service';
import { buildSemanticColorOverrides, buildThemeColorVars } from './theme.generator';
import { AuthService } from 'src/app/auth/services/auth.service';
import { AppLocalStorageService } from 'src/app/features/shared/storage/services/local-storage-services/app-local-storage.service';

// Zonder opgeslagen voorkeur volgt de app het besturingssysteem.
const DEFAULT_SETTINGS: ThemeSettings = {
    selectedThemeGuid: null,
    colorMode: colorModePreference.system,
};

const SYSTEM_DARK_QUERY = '(prefers-color-scheme: dark)';

@Injectable({ providedIn: 'root' })
export class ThemeService {
    private __api = inject(ThemeApiService);
    private __auth = inject(AuthService);
    // De geconsolideerde user-boom is de SSOT voor de theme-voorkeur (settings) en
    // de lokale theme-cache. Backend-synchronisatie loopt centraal via
    // UserStateSyncService; ThemeService reageert reactief op tree-wijzigingen.
    private __store = inject(AppLocalStorageService);

    private __theme$$ = signal<Theme>(this._loadCachedTheme());

    // OS-instelling licht/donker, live bijgewerkt (voor de voorkeur 'system').
    private __systemDark$$ = signal<boolean>(this.__systemDarkQuery()?.matches ?? false);

    Theme$$ = computed(() => this.__theme$$());
    private __settings$$ = computed<ThemeSettings>(() => ({
        ...DEFAULT_SETTINGS,
        ...(this.__store.appData$$.theme.settings() ?? {}),
    }));
    // De gekozen voorkeur (light/dark/system), voor de instellingen-UI.
    ColorModePreference$$ = computed<ColorModePreference>(() => this.__settings$$().colorMode);
    // De toegepaste modus: 'system' opgelost naar light/dark.
    ColorMode$$ = computed<ColorMode>(() => {
        const __preference = this.ColorModePreference$$();
        if (__preference !== colorModePreference.system) return __preference;
        return this.__systemDark$$() ? colorModePreference.dark : colorModePreference.light;
    });
    SelectedThemeGuid$$ = computed(() => this.__settings$$().selectedThemeGuid);

    constructor() {
        // Colour vars (palette ramps + accent families + surface set + semantic
        // overrides) depend on the theme AND the active color mode (isDark), so they
        // recompute whenever either changes.
        effect(() => this._applyColors(this.__theme$$(), this.ColorMode$$()));
        // Typography + shape literal overrides depend only on the theme.
        effect(() => this._applyTokens(this.__theme$$()));
        effect(() => this._applyColorMode(this.ColorMode$$()));
        this.__systemDarkQuery()?.addEventListener('change', __event => this.__systemDark$$.set(__event.matches));
        // Re-fetch de theme-JSON wanneer de onthouden selectie verandert (boot, of
        // na backend-hydratie op login) of bij token-wissel. Volgt alleen guid+token,
        // dus een colorMode-toggle triggert géén onnodige fetch.
        effect(() => {
            this.SelectedThemeGuid$$();
            this.__auth.token$$();
            void this._loadInitialTheme();
        });
    }

    SetColorMode(mode: ColorModePreference): void {
        this.__patchSettings({ colorMode: mode });
    }
    SetTheme(theme: Theme): void { this.__theme$$.set(theme); }

    /** Apply + remember a theme picked by the user from the Themes dropdown. */
    async SelectTheme(guid: string): Promise<void> {
        const __dto = await this.__api.Details_get(guid);
        if (!__dto?.json) return;
        const __theme = normalizeTheme(JSON.parse(__dto.json));
        const __applied = { ...__theme, id: __dto.guid ?? guid, name: __dto.name ?? __theme.name };
        this.__theme$$.set(__applied);
        this._cacheTheme(__applied);
        this.__patchSettings({ selectedThemeGuid: __dto.guid ?? guid });
    }

    /** Re-pull the applied theme (e.g. after login). Settings hydrateren centraal. */
    async SyncFromBackend(): Promise<void> {
        await this._loadInitialTheme();
    }

    // Apply the remembered theme if any, else the backend default. Quiet on failure
    // — theming is non-critical for app boot and /default is unauthenticated-safe
    // (falls back to the cached theme).
    protected async _loadInitialTheme(): Promise<void> {
        // /details vereist auth; anoniem (login-pagina) alleen het anonieme /default
        // raken — anders krijg je vermijdbare 401's.
        const __authed = !!this.__auth.Token;
        const __guid = __authed ? this.SelectedThemeGuid$$() : null;
        try {
            const __dto = __guid ? await this.__api.Details_get(__guid) : await this.__api.Default_get();
            if (!__dto?.json) return;
            const __theme = normalizeTheme(JSON.parse(__dto.json));
            const __applied = { ...__theme, id: __dto.guid ?? __theme.id, name: __dto.name ?? __theme.name };
            this.__theme$$.set(__applied);
            this._cacheTheme(__applied);
        } catch {
            // Backend unreachable — keep the cached theme (or hardcoded default).
        }
    }

    // Schrijf een deel-update van de theme-voorkeur naar de boom (SSOT). De
    // localStorage-persistentie + backend-push gebeuren automatisch (tree + sync).
    private __patchSettings(patch: Partial<ThemeSettings>): void {
        this.__store.appData$$.theme.settings.set({ ...this.__settings$$(), ...patch });
    }

    protected _loadCachedTheme(): Theme {
        const __raw = this.__store.appData$$.theme.cache();
        return __raw ? normalizeTheme(__raw) : DEFAULT_THEME;
    }

    // Persist de toegepaste theme lokaal zodat een offline/backend-down reload met de
    // echte kleuren boot i.p.v. de grijze DEFAULT_THEME (cache = lokaal-only).
    protected _cacheTheme(theme: Theme): void {
        this.__store.appData$$.theme.cache.set(theme);
    }

    protected _applyColorMode(colorMode: ColorMode): void {
        document.documentElement.setAttribute(MODE_ATTRIBUTES.colorMode.attr, colorMode);
    }

    // Null buiten de browser (SSR) of zonder matchMedia-ondersteuning.
    private __systemDarkQuery(): MediaQueryList | null {
        return typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(SYSTEM_DARK_QUERY) : null;
    }

    // Colour layer: per-base-color tonal ramps (--palette-*) + accent families
    // (--accent-*) + the shared surface set (--surface, …) for the active color mode,
    // plus the admin's per-category/per-token colour overrides (--color-*). All built
    // by the shared generator helpers so the live preview can't drift from this.
    protected _applyColors(theme: Theme, colorMode: ColorMode): void {
        const __root = document.documentElement;
        const __vars = buildThemeColorVars(theme, colorMode === 'dark');
        for (const [__name, __value] of Object.entries(__vars)) {
            __root.style.setProperty(__name, __value);
        }
        // Loop every remappable token so a cleared/absent override resets to the
        // generated _semantic.scss default (removeProperty reveals the stylesheet rule).
        const __overrides = buildSemanticColorOverrides(theme, colorMode === 'dark');
        for (const { entry: __entry } of remappableColorTokens()) {
            const __value = __overrides[__entry.cssVar];
            if (__value != null) __root.style.setProperty(__entry.cssVar, __value);
            else __root.style.removeProperty(__entry.cssVar);
        }
    }

    // Typography + shape token overrides, written straight to their semantic vars.
    protected _applyTokens(theme: Theme): void {
        const __root = document.documentElement;
        for (const __overrides of [theme.typography, theme.shape]) {
            for (const [__name, __value] of Object.entries(__overrides ?? {})) {
                const __entry = SEMANTIC_TOKENS[__name as SemanticTokenName];
                if (__entry && __value) __root.style.setProperty(__entry.cssVar, __value);
            }
        }
    }
}
